<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div class="steps-container" v-if="currentRound.steps" transition="fade">
          <div class="fitb-images-container">
            <div class="fitb-images">
              <article
                v-for="(step, stepIndex) in roundStepsWithImages"
                :key="stepIndex"
                style="height: 100px;"
              >
                <img
                  alt="Fill in image"
                  :class="`image-sentence-${step.activityImage?.image?.title}`"
                  :src="step.activityImage?.image?.src"
                  style="width: 100%; height: auto"
                />
              </article>
            </div>
          </div>
          <div class="fitb-container">
            <div
              v-for="(step, stepIndex) in currentRound.steps"
              :key="stepIndex"
              :class="`sentence-${stepIndex}`"
              v-show="stepIndex % 2 === 0"
              :style="{ visibility: progress.stepIndex >= stepIndex ? 'visible' : 'hidden' }"
            >
              <h3 transition="fade">
                {{ step.optionalWord?.split(step.answer)?.[0] }}
              </h3>

              <div class="prefix-sentence">
                <text-display-list>
                  <li transition="fade">
                    <blinking-cursor-text :text="step.answer" :text-step="stepIndex">
                    </blinking-cursor-text>
                  </li>
                </text-display-list>
              </div>

              <div class="sentence-expl">
                <text-display-list v-if="currentRound.steps[stepIndex + 1]">
                  <li transition="fade">
                    <blinking-cursor-text
                      :text="currentRound.steps[stepIndex + 1].answer"
                      :text-step="stepIndex + 1"
                    >
                    </blinking-cursor-text>
                  </li>
                </text-display-list>
              </div>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'Fill In The Blanks',
      instructionsLabel: ''
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  computed: {
    roundStepsWithImages() {
      return this.currentRound.steps.filter(step => step.activityImage?.image?.src);
    }
  },
  methods: {
    async loadActivity() {
      this.createRounds();
      this.instructionsLabel = this.activityData.instructions?.text ?? '';
      await Promise.all([this.loadImages(), this.loadSounds()]);
    },

    async loadImages() {
      const imagePaths = this.rounds[0].steps.map(step => step.activityImage?.image?.src);
      this.queueImage(imagePaths);
      return this.downloadImages();
    },

    async loadSounds() {
      const soundPaths = this.rounds[0].steps.map(step => step.activityAudio?.audio?.src);
      this.queueAudio(soundPaths);
      return this.downloadAudio();
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.finishStep();
      }
    },

    finishStep() {
      const { activityAudio, activityImage } = this.currentStep;

      if (activityAudio) {
        this.sndBellAndAfterPlayOtherHowl(this.cache.get(activityAudio.audio.src));
      } else {
        this.sndBell();
      }
      if (activityImage) {
        TweenMax.to(`.image-sentence-${activityImage?.image?.title}`, 1, {
          css: { autoAlpha: 1 }
        });
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
        return;
      }

      this.completeStep();
      this.setCurrentStep();
    },

    handleRoundEnd() {
      if (this.isEndOfActivity) {
        TweenMax.delayedCall(1, this.completeActivity);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

ul {
  list-style: none;
  margin-top: auto;
  padding-inline-start: 0px;
}

.activity-stage {
  height: 100%;
}

.steps-container {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
}

.chrome-os,
.tablet {
  .fitb-images-container {
    height: 100% !important;
  }

  .fitb-container {
    height: 100% !important;

    .medium {
      margin-bottom: 5px;
    }

    .sentence-0,
    .sentence-1,
    .sentence-2,
    .sentence-3,
    .sentence-4,
    .sentence-5 {
      margin-bottom: 0px;
      margin-top: 0px;

      .sentence-expl {
        margin-top: 0px !important;
      }
    }
  }

  .instructions-title {
    margin-bottom: 5px;
  }
}
</style>
